import { ClassifiedError, kindFromStatus } from '@momomail/shared';
import type { AdminUser, HealthInfo, InviteInfo, IssuedLink, LoginResult, MailerInfo, Me, PendingInvite } from '@momomail/shared';
import { client, load, save } from './bridge';
import { t } from './i18n';

/**
 * User-related APIs (multi-user servers): your own account, admin user management, and the no-login forgot password / reset / invite.
 * The no-login ones fetch the server URL directly (for the web version that's the current URL).
 */

const enc = encodeURIComponent;
const USERNAME_KEY = 'momomail.username';

/** Username used at the last login (just to save some typing). */
export const lastUsername = () => load(USERNAME_KEY) ?? '';
export const rememberUsername = (name: string) => save(USERNAME_KEY, name.trim());

/** Server URL: the configured URL in desktop remote mode, the current URL in the web version. */
export function serverBase(): string {
  return client.info.url || location.origin;
}

async function open<T>(method: string, path: string, body?: unknown, base = serverBase()): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    throw new ClassifiedError(navigator.onLine === false ? 'offline' : 'unreachable', String((error as Error)?.message ?? error));
  }
  const data = (await res.json().catch(() => null)) as unknown;
  const message = (data as { error?: string } | null)?.error;
  // If the backend explained it (invite expired, username taken…), pass it through as is; otherwise classify by status code
  if (!res.ok) throw message ? new Error(message) : new ClassifiedError(kindFromStatus(res.status), `HTTP ${res.status}`);
  if (path === '/api/health' && (!data || (data as { name?: string }).name !== 'momomail')) throw new ClassifiedError('not-momomail', 'health: not momomail');
  return data as T;
}

export const people = {
  // ---------- No login required ----------
  health: (base?: string) => open<HealthInfo>('GET', '/api/health', undefined, base),
  forgot: (login: string) => open<{ ok: true }>('POST', '/api/password/forgot', { login }),
  reset: (token: string, password: string) => open<{ ok: true }>('POST', '/api/password/reset', { token, password }),
  inviteInfo: (token: string) => open<InviteInfo>('GET', `/api/invites/${enc(token)}`),
  acceptInvite: (token: string, input: { username: string; password: string; recoveryEmail: string }) => open<LoginResult>('POST', `/api/invites/${enc(token)}/accept`, input),

  // ---------- Self ----------
  me: () => client.request<Me>('GET', '/api/me'),
  updateMe: (patch: { recoveryEmail: string | null }) => client.request<Me>('PATCH', '/api/me', patch),
  changePassword: (current: string, next: string) => client.request<{ ok: true }>('POST', '/api/me/password', { current, next }),

  // ---------- Admin ----------
  users: () => client.request<AdminUser[]>('GET', '/api/admin/users'),
  updateUser: (id: string, patch: { disabled?: boolean; isAdmin?: boolean }) => client.request<AdminUser>('PATCH', `/api/admin/users/${enc(id)}`, patch),
  removeUser: (id: string) => client.request<{ ok: true }>('DELETE', `/api/admin/users/${enc(id)}`),
  resetLink: (id: string) => client.request<IssuedLink>('POST', `/api/admin/users/${enc(id)}/reset-link`),
  invites: () => client.request<PendingInvite[]>('GET', '/api/admin/invites'),
  invite: (isAdmin: boolean, note: string) => client.request<IssuedLink>('POST', '/api/admin/invites', { isAdmin, note }),
  revokeInvite: (id: string) => client.request<{ ok: true }>('DELETE', `/api/admin/invites/${enc(id)}`),
  mailer: () => client.request<MailerInfo>('GET', '/api/admin/mailer'),
  setMailer: (accountId: string | null) => client.request<MailerInfo>('PUT', '/api/admin/mailer', { accountId }),
};

/** Copy to clipboard (falls back to the old method when the Clipboard API isn't available). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

export const formatTime = (t: number) => new Date(t).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
